/*
 * Pretty Frank cart drawer (PF 2.9, Oct 13 2026)
 * - Runs once even though the theme loads this file twice
 *   (cart-drawer-section + the header cart-drawer snippet).
 * - Taps update the screen right away. Shopify is told in the background,
 *   then the drawer quietly syncs to Shopify's real numbers.
 * - Quick repeated +/- taps are sent as one request.
 * - "You May Also Like" only reloads when the products in the cart change.
 * - Never uses Cart API "sections" bundling (it caused 503s on this store).
 */
(function () {
  if (window.__pfCartDrawerInit) return;
  window.__pfCartDrawerInit = true;

  function start() {
    const body = document.body;
    const root = () => document.getElementById("cart_drawer_section");
    const q = (sel) => {
      const r = root();
      return r ? r.querySelector(sel) : null;
    };
    const qa = (sel) => {
      const r = root();
      return r ? Array.from(r.querySelectorAll(sel)) : [];
    };

    // ── Money helpers (store currency is USD, "$1,234.56") ──
    function parseMoney(text) {
      const n = Number(String(text || "").replace(/[^0-9.]/g, ""));
      return isNaN(n) ? 0 : n;
    }
    function formatMoney(n) {
      const v = Math.max(0, Math.round(n * 100) / 100);
      return "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    // ── Open / close / spinner ──
    function toggleCartDrawer(isOpen) {
      const r = root();
      if (!r) return;
      r.classList.toggle("cart-drawer--active", isOpen);
      body.classList.toggle("scroll_locked", isOpen);
      if (isOpen) ensureRecommendations();
    }
    function setLoading(on) {
      const r = root();
      if (r) r.classList.toggle("loader_active", on);
    }

    // ── Cart count everywhere on the page ──
    function setCount(count) {
      const c = Number(count) || 0;
      document.querySelectorAll("#CartCount span, .cart-bubble__text-count").forEach((el) => {
        el.textContent = c;
      });
      const counter = q(".cart--counter--drawer p");
      if (counter) counter.textContent = "Your cart ( " + c + " )";
    }
    function currentCount() {
      return qa(".cart-drawer-item").reduce((sum, item) => {
        if (item.style.display === "none") return sum;
        const input = item.querySelector(".cart-drawer-quantity-selector input");
        return sum + (Number(input && input.value) || 0);
      }, 0);
    }

    // ── Subtotal / checkout button / free shipping bar ──
    function currentTotal() {
      const el = q(".cart-total");
      return el ? parseMoney(el.textContent) : 0;
    }
    function setTotal(total) {
      qa(".cart-total").forEach((el) => {
        el.textContent = formatMoney(total);
        el.dataset.total = total.toFixed(2);
      });
      const btn = q("#submit-cart-btn");
      if (btn) {
        const textNode = Array.from(btn.childNodes)
          .reverse()
          .find((n) => n.nodeType === 3 && n.textContent.trim());
        if (textNode) textNode.textContent = " Secure Checkout • " + formatMoney(total);
      }
      updateThresholds(total);
    }
    function dimTotals(on) {
      qa(".cart-total, #submit-cart-btn").forEach((el) => {
        el.style.opacity = on ? "0.5" : "";
      });
    }

    function updateThresholds(cartTotal) {
      const wrapper = q(".cart-drawer-thresholds-wrapper");
      if (!wrapper) return;
      const gifts = qa(".cart-drawer-thresholds-gift");
      const message = q(".cart-drawer-thresholds-message");
      const bar = q(".cart-drawer-thresholds-bar");
      if (!gifts.length || !message || !bar) return;

      let maxAmount = 0;
      const remaining = [];
      gifts.forEach((gift) => {
        const amount = parseFloat(gift.dataset.amount);
        maxAmount = Math.max(maxAmount, amount);
        if (cartTotal >= amount) {
          gift.classList.add("threshold-reached");
        } else {
          gift.classList.remove("threshold-reached");
          remaining.push("$" + (amount - cartTotal).toFixed(2) + " more for " + gift.dataset.message);
        }
      });
      message.textContent = remaining.length
        ? "Add " + remaining.join(" plus ")
        : "Congratulations! You've unlocked all offers.";
      bar.style.width = Math.min((cartTotal / maxAmount) * 100, 100) + "%";
    }

    // ── Error message inside the drawer ──
    function showError(text) {
      const r = root();
      if (!r || !text) return;
      let box = r.querySelector(".pf-cart-error");
      if (!box) {
        box = document.createElement("div");
        box.className = "pf-cart-error";
        box.setAttribute("role", "alert");
        box.style.cssText =
          "background:#fdecec;color:#8a1c1c;font-size:13px;line-height:1.4;padding:10px 12px;border-radius:8px;margin:0 0 14px;";
        const scroll = r.querySelector(".scrolable_cart_part");
        if (scroll) scroll.insertBefore(box, scroll.firstChild);
        else r.appendChild(box);
      }
      box.textContent = text;
      clearTimeout(showError._t);
      showError._t = setTimeout(() => box.remove(), 6000);
    }

    // ── Network: one retry on 5xx, JSON-safe ──
    async function request(url, options, tries) {
      const attempts = tries || 2;
      let last;
      for (let i = 0; i < attempts; i++) {
        try {
          const res = await fetch(url, options);
          if (res.status >= 500 && i < attempts - 1) {
            await new Promise((r) => setTimeout(r, 400 * (i + 1)));
            continue;
          }
          return res;
        } catch (err) {
          last = err;
          if (i < attempts - 1) await new Promise((r) => setTimeout(r, 400 * (i + 1)));
        }
      }
      throw last || new Error("Network error");
    }
    async function readJSON(res) {
      try {
        return await res.json();
      } catch (e) {
        return {};
      }
    }
    function errorText(data) {
      return (data && (data.description || data.message)) || "Something went wrong. Please try again.";
    }

    // ── Background queue: requests go one at a time, screen never waits ──
    let chain = Promise.resolve();
    let busy = 0;
    let lastError = null;
    const pendingQty = new Map(); // line key -> quantity not yet sent
    let flushTimer = null;

    function enqueue(task) {
      busy++;
      chain = chain
        .then(task)
        .catch((err) => {
          lastError = lastError || "Connection problem. Please try again.";
          console.error("Cart update failed:", err);
        })
        .then(() => {
          busy--;
          settle();
        });
      return chain;
    }

    function settle() {
      if (busy > 0 || pendingQty.size) return;
      const err = lastError;
      lastError = null;
      refreshDrawer({ error: err });
    }

    function scheduleQtyFlush() {
      clearTimeout(flushTimer);
      flushTimer = setTimeout(() => {
        if (!pendingQty.size) return;
        const updates = Object.fromEntries(pendingQty);
        pendingQty.clear();
        enqueue(async () => {
          const res = await request("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ updates }),
          });
          if (!res.ok) lastError = errorText(await readJSON(res));
        });
      }, 250);
    }

    // ── Quantity +/- (instant) ──
    function onQuantity(button) {
      if (button.disabled) return;
      const item = button.closest(".cart-drawer-item");
      if (!item || item.dataset.pfLocked === "1") return;
      const key = item.dataset.lineItemKey;
      const input = item.querySelector(".cart-drawer-quantity-selector input");
      if (!key || !input) return;

      const oldQty = Number(input.value) || 1;
      const newQty = button.classList.contains("cart-drawer-quantity-selector-plus") ? oldQty + 1 : oldQty - 1;
      if (newQty < 1) return;

      input.value = newQty;
      const minus = item.querySelector(".cart-drawer-quantity-selector-minus");
      if (minus) minus.disabled = newQty < 2;
      const plan = item.querySelector(".cart-item-plan");
      if (plan) plan.dataset.quantity = newQty;

      const priceEl = item.querySelector(".cart_dra_item_price");
      let delta = 0;
      if (priceEl) {
        const line = parseMoney(priceEl.textContent);
        const newLine = (line / oldQty) * newQty;
        delta = newLine - line;
        priceEl.textContent = formatMoney(newLine);
      }
      setTotal(currentTotal() + delta);
      setCount(currentCount());

      pendingQty.set(key, newQty);
      scheduleQtyFlush();
    }

    // ── Remove item (instant) ──
    function onDelete(button) {
      const item = button.closest(".cart-drawer-item");
      const key = button.dataset.lineItemKey || (item && item.dataset.lineItemKey);
      if (!item || !key) return;

      const priceEl = item.querySelector(".cart_dra_item_price");
      const line = priceEl ? parseMoney(priceEl.textContent) : 0;
      item.style.display = "none";
      setTotal(currentTotal() - line);
      setCount(currentCount());

      pendingQty.delete(key);
      enqueue(async () => {
        const res = await request("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ updates: { [key]: 0 } }),
        });
        if (!res.ok) lastError = errorText(await readJSON(res));
      });
    }

    // ── One-time / Subscribe toggle and frequency (instant toggle, price syncs after) ──
    function changePlan(wrapper, sellingPlanId) {
      const key = wrapper && wrapper.dataset.lineItemKey;
      if (!key) return;
      const item = wrapper.closest(".cart-drawer-item");

      // Send any waiting +/- taps first so the plan change uses the right quantity.
      if (pendingQty.size) {
        clearTimeout(flushTimer);
        const updates = Object.fromEntries(pendingQty);
        pendingQty.clear();
        enqueue(async () => {
          const res = await request("/cart/update.js", {
            method: "POST",
            headers: { "Content-Type": "application/json", Accept: "application/json" },
            body: JSON.stringify({ updates }),
          });
          if (!res.ok) lastError = errorText(await readJSON(res));
        });
      }

      // The line's key changes when its plan changes, so lock it until the drawer re-syncs.
      if (item) {
        item.dataset.pfLocked = "1";
        const prices = item.querySelector(".cart_drawer_prices");
        if (prices) prices.style.opacity = "0.5";
      }
      dimTotals(true);

      const quantity = Number(wrapper.dataset.quantity) || 1;
      enqueue(async () => {
        const res = await request("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({
            id: key,
            quantity: quantity,
            selling_plan: sellingPlanId ? Number(sellingPlanId) : null,
          }),
        });
        if (!res.ok) lastError = errorText(await readJSON(res));
      });
    }

    function onPlanOption(button) {
      if (button.classList.contains("is-active")) return;
      const wrapper = button.closest(".cart-item-plan");
      if (!wrapper) return;
      const item = wrapper.closest(".cart-drawer-item");
      if (item && item.dataset.pfLocked === "1") return;
      wrapper.querySelectorAll(".cart-plan-option").forEach((b) => {
        b.classList.toggle("is-active", b === button);
      });
      changePlan(wrapper, button.dataset.sellingPlan);
    }

    // ── Pull the real drawer from Shopify (one request) ──
    let refreshSeq = 0;
    async function refreshDrawer(opts) {
      const options = opts || {};
      const seq = ++refreshSeq;
      let html;
      try {
        const res = await request("/?section_id=cart-drawer-section", {});
        html = await res.text();
      } catch (err) {
        console.error("Error updating cart drawer:", err);
        if (options.error) showError(options.error);
        setLoading(false);
        return;
      }
      // A newer refresh started, or the shopper tapped again: let the later sync win.
      if (seq !== refreshSeq || busy > 0 || pendingQty.size) return;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const fresh = doc.querySelector("#cart_drawer_section") || doc.querySelector(".cart-drawer");
      const r = root();
      if (r && fresh) r.innerHTML = fresh.innerHTML;

      const header = q(".cart-drawer-header-right-items");
      setCount(header ? header.dataset.count : currentCount());
      const totalEl = q(".cart-total");
      updateThresholds(totalEl ? parseFloat(String(totalEl.dataset.total || "0").replace(/,/g, "")) : 0);
      setLoading(false);
      if (options.error) showError(options.error);
      renderRecommendations();
    }

    // ── "You May Also Like" (only reloads when cart products change) ──
    const rec = { sig: null, html: null, empty: false, loading: null };

    function cartSignature() {
      return qa(".cart-drawer-item")
        .filter((item) => item.style.display !== "none")
        .map((item) => String(item.dataset.lineItemKey || "").split(":")[0])
        .sort()
        .join(",");
    }

    function paintRecommendations() {
      const list = q(".cart-drawer-recommendation-list");
      if (!list) return;
      const wrapper = list.closest(".cart-drawer-recommendations");
      if (rec.empty) {
        if (wrapper) wrapper.style.display = "none";
        return;
      }
      if (!rec.html) return;
      if (wrapper) wrapper.style.display = "";
      list.innerHTML = rec.html;
      list.classList.add("cart-drawer-recommendation-list--ready");
      initCartDrawerSlider();
    }

    function renderRecommendations() {
      const list = q(".cart-drawer-recommendation-list");
      if (!list) return;
      const sig = cartSignature();
      if (sig && sig === rec.sig && (rec.html || rec.empty)) {
        paintRecommendations();
        return;
      }
      loadRecommendations(sig);
    }

    function ensureRecommendations() {
      const list = q(".cart-drawer-recommendation-list");
      if (!list || list.classList.contains("cart-drawer-recommendation-list--ready")) return;
      renderRecommendations();
    }

    async function loadRecommendations(sig) {
      if (rec.loading === sig) return;
      rec.loading = sig;
      try {
        const cartRes = await request("/cart.js", { headers: { Accept: "application/json" } });
        const cart = await readJSON(cartRes);
        const items = cart.items || [];
        if (!items.length) return;

        const cartProductIds = items.map((item) => item.product_id);
        const uniqueIds = [...new Set(cartProductIds)].slice(0, 3);
        const fetchRecs = (id, intent) =>
          fetch("/recommendations/products.json?product_id=" + id + "&limit=8&intent=" + intent)
            .then((res) => (res.ok ? res.json() : { products: [] }))
            .catch(() => ({ products: [] }));

        const [complementary, related] = await Promise.all([
          Promise.all(uniqueIds.map((id) => fetchRecs(id, "complementary"))),
          Promise.all(uniqueIds.map((id) => fetchRecs(id, "related"))),
        ]);
        if (rec.loading !== sig) return;

        const scored = new Map();
        const addToScore = (data, bonus) => {
          (data.products || []).forEach((product, position) => {
            if (cartProductIds.includes(product.id)) return;
            const previous = scored.get(product.id);
            const score = (previous ? previous.score : 0) + bonus + (8 - position);
            scored.set(product.id, { product, score });
          });
        };
        complementary.forEach((data) => addToScore(data, 100));
        related.forEach((data) => addToScore(data, 0));

        const products = [...scored.values()]
          .sort((a, b) => b.score - a.score)
          .map((entry) => entry.product)
          .slice(0, 4);

        rec.sig = sig;
        rec.empty = !products.length;
        rec.html = products.map(createProductHTML).join("");
        paintRecommendations();
      } catch (error) {
        console.error("Failed to fetch recommendations:", error);
      } finally {
        if (rec.loading === sig) rec.loading = null;
      }
    }

    function createProductHTML(product) {
      const tagsHTML = (product.tags || []).map(createTagHTML).join("");
      const variantId = product.variants && product.variants[0] ? product.variants[0].id : "";
      return (
        '<li class="cart-drawer-recommendation-item resource-list__item">' +
        '<a class="cart-drawer-recommendation-item-image" href="' + product.url + '">' +
        '<img src="' + product.featured_image + '" alt="' + String(product.title).replace(/"/g, "&quot;") + '">' +
        '<div class="product-multi-tags">' + tagsHTML + "</div>" +
        "</a>" +
        "<h3>" + product.title + "</h3>" +
        "<p><span>$" + (product.price / 100).toFixed(2) + "</span>" +
        (product.compare_at_price
          ? '<span class="cart_drawer_recommend_compare_price">$' + (product.compare_at_price / 100).toFixed(2) + "</span>"
          : "") +
        "</p>" +
        '<form method="post" action="/cart/add" accept-charset="UTF-8" enctype="multipart/form-data" novalidate>' +
        '<input type="hidden" name="id" value="' + variantId + '">' +
        '<input type="hidden" name="quantity" value="1">' +
        '<button type="submit" name="add" aria-label="Add to cart" class="collection-btn btn product-form__cart-submitRecommended" data-add-to-cart tabindex="0">' +
        "<span data-add-to-cart-text>Add to cart</span>" +
        '<span class="hide" data-loader><svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-spinner" viewBox="0 0 20 20"><path d="M7.229 1.173a9.25 9.25 0 1 0 11.655 11.412 1.25 1.25 0 1 0-2.4-.698 6.75 6.75 0 1 1-8.506-8.329 1.25 1.25 0 1 0-.75-2.385z" fill="#919EAB"></path></svg></span>' +
        "</button>" +
        "</form>" +
        "</li>"
      );
    }

    function createTagHTML(tag) {
      const tagClassMap = {
        "BEST VALUE": "best-value",
        "BEST SELLER": "best-seller",
        "NEW ARRIVAL": "save new",
        "LIMITED QUANTITY": "best-value",
      };
      const tagClass = tagClassMap[tag] || "";
      if (tag.includes("Save")) return '<span class="save">' + tag.replace("Save ", "") + " OFF</span>";
      if (tag === "Free Gift") return "";
      return '<span class="best-tag ' + tagClass + '">' + tag.trim() + "</span>";
    }

    // ── Add to cart (product page buttons + "You May Also Like") ──
    async function addToCart(form, button) {
      if (button.getAttribute("aria-disabled") === "true") return;
      button.setAttribute("aria-disabled", "true");
      const text = button.querySelector("[data-add-to-cart-text]");
      const loader = button.querySelector("[data-loader]");
      if (text) text.classList.add("hide");
      if (loader) loader.classList.remove("hide");

      // Open the drawer right away instead of waiting.
      toggleCartDrawer(true);
      setLoading(true);

      enqueue(async () => {
        const res = await request("/cart/add.js", {
          method: "POST",
          headers: { Accept: "application/json" },
          body: new FormData(form),
        });
        if (!res.ok) lastError = errorText(await readJSON(res));
      }).then(() => {
        button.removeAttribute("aria-disabled");
        if (text) text.classList.remove("hide");
        if (loader) loader.classList.add("hide");
      });
    }

    // ── One set of listeners for everything (survives drawer re-renders) ──
    document.addEventListener("click", (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;

      const recBtn = target.closest(".product-form__cart-submitRecommended");
      const pageBtn = !recBtn && target.closest(".product-form__cart-submit");
      if (recBtn || pageBtn) {
        const btn = recBtn || pageBtn;
        const form = btn.closest("form");
        if (!form) return;
        e.preventDefault();
        addToCart(form, btn);
        return;
      }

      const cartLink = target.closest('a[href="/cart"]');
      if (cartLink) {
        e.preventDefault();
        toggleCartDrawer(true);
        // Drawer already has the cart from page load; sync quietly in the background.
        if (busy === 0 && !pendingQty.size) refreshDrawer();
        return;
      }

      const r = root();
      if (!r || !r.contains(target)) return;

      if (target.closest(".cart-drawer-overlay") || target.closest(".cart-drawer-header-right-close")) {
        toggleCartDrawer(false);
        return;
      }

      const qtyBtn = target.closest(".cart-drawer-quantity-selector button");
      if (qtyBtn) {
        e.preventDefault();
        onQuantity(qtyBtn);
        return;
      }

      const delBtn = target.closest(".cart-drawer-item-delete");
      if (delBtn) {
        e.preventDefault();
        onDelete(delBtn);
        return;
      }

      const planBtn = target.closest(".cart-item-plan .cart-plan-option");
      if (planBtn) {
        e.preventDefault();
        onPlanOption(planBtn);
      }
    });

    document.addEventListener("change", (e) => {
      const select = e.target instanceof Element ? e.target.closest(".cart-item-plan-frequency") : null;
      const r = root();
      if (!select || !r || !r.contains(select)) return;
      const wrapper = select.closest(".cart-item-plan");
      const item = wrapper && wrapper.closest(".cart-drawer-item");
      if (item && item.dataset.pfLocked === "1") return;
      changePlan(wrapper, select.value);
    });

    // Product forms elsewhere in the theme fire this after adding to cart.
    window.addEventListener("custom:open-cart-drawer", () => {
      toggleCartDrawer(true);
      setLoading(true);
      if (busy === 0 && !pendingQty.size) refreshDrawer();
    });

    // Initial state from the page-load render (no network calls on page load).
    const initialTotal = q(".cart-total");
    updateThresholds(initialTotal ? parseFloat(String(initialTotal.dataset.total || "0").replace(/,/g, "")) : 0);
  }

  function initCartDrawerSlider() {
    if (typeof jQuery === "undefined" || typeof jQuery.fn.slick === "undefined") return;
    const $slider = jQuery("#cart_drawer_section .cart-drawer-recommendation-list--ready");
    if (!$slider.length) return;
    if ($slider.hasClass("slick-initialized")) {
      try {
        $slider.slick("unslick");
      } catch (err) {}
    }
    const imgs = $slider.find("img").get();
    let loaded = 0;
    const done = () => {
      loaded++;
      if (loaded === imgs.length) doInit();
    };
    if (!imgs.length) doInit();
    else
      imgs.forEach((img) => {
        if (img.complete && img.naturalWidth !== 0) done();
        else {
          img.addEventListener("load", done, { once: true });
          img.addEventListener("error", done, { once: true });
        }
      });
    function doInit() {
      if ($slider.hasClass("slick-initialized")) return;
      $slider.slick({
        infinite: false,
        slidesToShow: 2.5,
        slidesToScroll: 1,
        arrows: true,
        dots: false,
        swipeToSlide: true,
        prevArrow: '<button type="button" class="slick-prev">‹</button>',
        nextArrow: '<button type="button" class="slick-next">›</button>',
        responsive: [{ breakpoint: 768, settings: { slidesToShow: 2.5 } }],
      });
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
